import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { Alert, Box, Chip, CircularProgress, MenuItem, Paper, Stack, TextField, Typography } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import React, { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useStore } from 'src/stores';
const KINDS = {
  TurnedOn: 'Turned on',
  TurnedOff: 'Turned off',
  UtilityAdded: 'Utility added',
  UtilityRemoved: 'Utility removed'
};
const OPTIONS = {
  ApPush: 'AP Push',
  BillAutomation: 'Bill Automation'
};
const REASONS = {
  PlanEnded: 'Bill Automation plan ended',
  AppFolioDisconnected: 'AppFolio disconnected'
};
const SEGMENTS = ['Turned on', 'Turned off', 'Not active anymore'];
const isOn = c => c.kind === 'TurnedOn' || c.kind === 'UtilityAdded';

/** Utilities turned on or off for AP Push or Bill Automation, and switches that no longer count. */
export const ChangesView = observer(() => {
  var _state$data2, _state$data3;
  const store = useStore();
  const _useState = useState(30),
    _useState2 = _slicedToArray(_useState, 2),
    days = _useState2[0],
    setDays = _useState2[1];
  const _useState3 = useState(SEGMENTS[0]),
    _useState4 = _slicedToArray(_useState3, 2),
    segment = _useState4[0],
    setSegment = _useState4[1];
  const _useState5 = useState({
      loading: true,
      error: '',
      data: null
    }),
    _useState6 = _slicedToArray(_useState5, 2),
    state = _useState6[0],
    setState = _useState6[1];
  useEffect(() => {
    let cancelled = false;
    setState(s => _objectSpread(_objectSpread({}, s), {}, {
      loading: true,
      error: ''
    }));
    store.admin.getBillQueueChangesAsync.run(days).then(resp => {
      if (cancelled) return;
      const data = (resp === null || resp === void 0 ? void 0 : resp.response) || resp;
      setState(store.admin.getBillQueueChangesAsync.isError || !Array.isArray(data === null || data === void 0 ? void 0 : data.changes) ? {
        loading: false,
        error: 'Could not load the changes.',
        data: null
      } : {
        loading: false,
        error: '',
        data
      });
    });
    return () => {
      cancelled = true;
    };
  }, [store, days]);
  const changes = useMemo(() => {
    var _state$data;
    return ((_state$data = state.data) === null || _state$data === void 0 ? void 0 : _state$data.changes) || [];
  }, [state.data]);
  const on = useMemo(() => changes.filter(isOn), [changes]);
  const off = useMemo(() => changes.filter(c => !isOn(c)), [changes]);
  const inactive = ((_state$data2 = state.data) === null || _state$data2 === void 0 ? void 0 : _state$data2.inactive) || [];
  const counts = {
    'Turned on': on.length,
    'Turned off': off.length,
    'Not active anymore': inactive.length
  };
  const changeColumns = [{
    field: 'changedOn',
    headerName: 'When',
    width: 150,
    renderCell: _ref => {
      let row = _ref.row;
      return /*#__PURE__*/React.createElement(Stack, {
        direction: "row",
        spacing: 6,
        alignItems: "center"
      }, /*#__PURE__*/React.createElement("span", null, new Date(row.changedOn).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric'
      })), isOn(row) && !row.seen ? /*#__PURE__*/React.createElement(Chip, {
        size: "small",
        color: "error",
        label: "New",
        sx: {
          height: 18
        }
      }) : null);
    }
  }, {
    field: 'companyName',
    headerName: 'Company',
    flex: 1,
    minWidth: 180
  }, {
    field: 'propertyAddress',
    headerName: 'Property',
    flex: 1.2,
    minWidth: 200
  }, {
    field: 'utilityTypeName',
    headerName: 'Utility',
    width: 110
  }, {
    field: 'providerName',
    headerName: 'Provider',
    flex: 1,
    minWidth: 160
  }, {
    field: 'option',
    headerName: 'Option',
    width: 150,
    valueGetter: _ref2 => {
      let row = _ref2.row;
      return OPTIONS[row.option] || row.option;
    }
  }, {
    field: 'kind',
    headerName: 'Change',
    width: 150,
    valueGetter: _ref3 => {
      let row = _ref3.row;
      return KINDS[row.kind] || row.kind;
    }
  }];
  const inactiveColumns = [{
    field: 'companyName',
    headerName: 'Company',
    flex: 1,
    minWidth: 200
  }, {
    field: 'option',
    headerName: 'Option',
    width: 160,
    valueGetter: _ref4 => {
      let row = _ref4.row;
      return OPTIONS[row.option] || row.option;
    }
  }, {
    field: 'reason',
    headerName: 'Why',
    flex: 1,
    minWidth: 200,
    valueGetter: _ref5 => {
      let row = _ref5.row;
      return REASONS[row.reason] || row.reason;
    }
  }, {
    field: 'utilities',
    headerName: 'Utilities',
    width: 110,
    type: 'number'
  }];
  return /*#__PURE__*/React.createElement(Paper, {
    sx: {
      padding: '16px'
    }
  }, /*#__PURE__*/React.createElement(Stack, {
    direction: "row",
    spacing: 6,
    flexWrap: "wrap",
    useFlexGap: true,
    alignItems: "center",
    sx: {
      marginBottom: '12px'
    }
  }, SEGMENTS.map(s => /*#__PURE__*/React.createElement(Chip, {
    key: s,
    label: "".concat(s, " (").concat(counts[s], ")"),
    color: segment === s ? 'primary' : 'default',
    variant: segment === s ? 'filled' : 'outlined',
    onClick: () => setSegment(s)
  })), /*#__PURE__*/React.createElement(Box, {
    sx: {
      flexGrow: 1
    }
  }), /*#__PURE__*/React.createElement(TextField, {
    select: true,
    size: "small",
    label: "Window",
    value: days,
    onChange: e => setDays(Number(e.target.value)),
    sx: {
      minWidth: 150
    }
  }, [7, 30, 90].map(d => /*#__PURE__*/React.createElement(MenuItem, {
    key: d,
    value: d
  }, "Last ".concat(d, " days"))))), state.error ? /*#__PURE__*/React.createElement(Alert, {
    severity: "error"
  }, state.error) : null, state.loading ? /*#__PURE__*/React.createElement(Box, {
    sx: {
      padding: '32px',
      textAlign: 'center'
    }
  }, /*#__PURE__*/React.createElement(CircularProgress, null)) : null, !state.loading && !state.error ? segment === 'Not active anymore' ? /*#__PURE__*/React.createElement(DataGrid, {
    autoHeight: true,
    rows: inactive,
    columns: inactiveColumns,
    getRowId: r => "".concat(r.companyId, "-").concat(r.option),
    disableRowSelectionOnClick: true,
    hideFooter: inactive.length <= 25
  }) : /*#__PURE__*/React.createElement(DataGrid, {
    autoHeight: true,
    rows: segment === 'Turned on' ? on : off,
    columns: changeColumns,
    getRowId: r => "".concat(r.propertyUtilityId, "-").concat(r.option, "-").concat(r.kind, "-").concat(r.changedOn),
    disableRowSelectionOnClick: true,
    initialState: {
      pagination: {
        paginationModel: {
          page: 0,
          pageSize: 25
        }
      }
    },
    pageSizeOptions: [25, 50, 100]
  }) : null, /*#__PURE__*/React.createElement(Typography, {
    variant: "caption",
    color: "text.secondary",
    sx: {
      display: 'block',
      marginTop: '8px'
    }
  }, "Who made each change is not recorded yet. It will be once utility setup keeps its own change history.", (_state$data3 = state.data) !== null && _state$data3 !== void 0 && _state$data3.lastSeenOn ? " Last marked seen ".concat(new Date(state.data.lastSeenOn).toLocaleString(), " by ").concat(state.data.lastSeenBy, ".") : ''));
});